import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";

import { Check } from "icons";

import {
  StyledCheckboxContainer,
  StyledCheckboxError,
  StyledCheckboxField,
  StyledCheckboxLabel,
} from "styles/components/inputs/Checkbox";

type onChangeWithValue = (value: boolean, name?: string) => void;
type onChangeWithoutValue = () => void;

interface Props {
  checked?: boolean;
  disabled?: boolean;
  name?: string;
  label?: string;
  className?: string;
  error?: boolean;
  errorText?: string;
  dataTestId?: string;

  onChange?: onChangeWithValue | onChangeWithoutValue;
}

const Checkbox = forwardRef<HTMLInputElement, Props>(
  (
    {
      disabled,
      checked = false,
      label,
      name,
      error,
      className,
      errorText = "Please check this box",
      dataTestId,
      onChange,
    },
    ref
  ) => {
    const [isChecked, setChecked] = useState<boolean>(checked ?? false);

    const checkboxFieldClassName = clsx(["checkbox-field", className]);

    const checkboxClassName = clsx([
      "checkbox",
      {
        checkbox_error: error,
      },
    ]);

    useEffect(() => setChecked(checked), [checked]);

    const handleChange = useCallback(() => {
      const newValue = !isChecked;

      setChecked(newValue);

      if (onChange) onChange(newValue, name);
    }, [isChecked, name, onChange]);

    const renderedLabel = useMemo(
      () =>
        label ? (
          <StyledCheckboxLabel
            variant="bodyL"
            data-testid={dataTestId && `${dataTestId}-label`}
            className="radio__label"
          >
            {label}
          </StyledCheckboxLabel>
        ) : null,
      [dataTestId, label]
    );

    const renderedErrorText = error && errorText && (
      <StyledCheckboxError
        variant="bodyM"
        fontWeight={300}
        data-testid={dataTestId && `${dataTestId}-error`}
      >
        {errorText}
      </StyledCheckboxError>
    );

    return (
      <StyledCheckboxField
        className={checkboxFieldClassName}
        data-testid={dataTestId}
      >
        <StyledCheckboxContainer
          className={checkboxClassName}
          data-testid={dataTestId && `${dataTestId}-container`}
        >
          <input
            ref={ref}
            type="checkbox"
            className="checkbox__real"
            disabled={disabled}
            checked={isChecked}
            onChange={handleChange}
            key="checkbox"
            name={name}
            data-testid={dataTestId && `${dataTestId}-real-checkbox`}
          />
          <span
            className="checkbox__custom"
            data-testid={dataTestId && `${dataTestId}-custom-checkbox`}
          >
            <span className="checkbox__icon">
              <Check />
            </span>
          </span>
          {renderedLabel}
        </StyledCheckboxContainer>
        {renderedErrorText}
      </StyledCheckboxField>
    );
  }
);

export default Checkbox;
